import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { h2hAPI } from '../api/endpoints';
import { useWallet } from '../context/WalletContext';
import { formatXPUNT, formatTimeAgo, formatDate, getUserId } from '../utils/helpers';
import { 
  FaArrowLeft, FaUsers, FaTrophy, FaClock, 
  FaHandshake, FaCheck, FaTimes, FaCopy, FaShare,
  FaChartBar, FaCoins, FaUser
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './H2HDetail.css';

const H2HDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { balance, fetchBalance } = useWallet();
  
  // State
  const [loading, setLoading] = useState(true);
  const [game, setGame] = useState(null);
  const [hasJoined, setHasJoined] = useState(false);
  const [userStake, setUserStake] = useState(null);
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [stakeAmount, setStakeAmount] = useState('');
  const [selectedOutcome, setSelectedOutcome] = useState('');
  const [claimStatus, setClaimStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const currentUserId = getUserId();

  // ==================== FETCH GAME DATA ====================
  const fetchGameData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch game details
      let gameData = null;
      
      try {
        const response = await h2hAPI.getSingle(id);
        
        // Handle different response structures
        if (response.data?.data) {
          gameData = response.data.data;
        } else if (response.data && response.data.id) {
          gameData = response.data;
        }
      } catch (singleError) {
        console.log('Single endpoint failed, trying list:', singleError);
      }
      
      // Fallback to list if single didn't work
      if (!gameData) {
        const listRes = await h2hAPI.getList({ per_page: 100 });
        const gamesList = listRes.data?.data || listRes.data || [];
        gameData = gamesList.find(g => String(g.id) === String(id));
      }
      
      if (!gameData) {
        toast.error('Game not found');
        navigate('/h2h');
        return;
      }

      // Enrich with computed state
      const kickoffTime = gameData.kickoff_time || gameData.start_time || gameData.game_time;
      const kickoffTimestamp = kickoffTime ? new Date(kickoffTime).getTime() : 0;
      const now = Date.now();
      const hasStarted = kickoffTimestamp > 0 && kickoffTimestamp <= now;
      const isEnded = gameData.status === 'completed' || gameData.status === 'refunded';
      const isLive = !isEnded && hasStarted;
      
      // Check if we have a real score
      const hasValidHomeScore = 
        gameData.home_score !== null && 
        gameData.home_score !== undefined && 
        gameData.home_score !== '';
      const hasValidAwayScore = 
        gameData.away_score !== null && 
        gameData.away_score !== undefined && 
        gameData.away_score !== '';
      
      const hasScore = hasValidHomeScore && hasValidAwayScore && (isLive || isEnded);
      
      const enrichedGame = {
        ...gameData,
        _hasStarted: hasStarted,
        _isEnded: isEnded,
        _isLive: isLive,
        _hasScore: hasScore,
      };
      
      setGame(enrichedGame);

      // Check if current user has joined
      if (isAuthenticated && currentUserId) {
        // First try dedicated endpoint
        try {
          const joinedRes = await h2hAPI.checkJoined(id);
          if (joinedRes.data) {
            setHasJoined(joinedRes.data.joined || false);
            setUserStake(joinedRes.data.stake || null);
          }
        } catch (joinError) {
          console.log('checkJoined endpoint failed, using fallback:', joinError);
          
          // Fallback: check from game data
          if (gameData?.stakes) {
            let foundStake = null;
            
            for (const [outcome, data] of Object.entries(gameData.stakes)) {
              if (data.users && Array.isArray(data.users)) {
                const userEntry = data.users.find(u => 
                  String(u.user_id) === String(currentUserId)
                );
                if (userEntry) {
                  foundStake = {
                    outcome: outcome,
                    stake_amount: userEntry.stake_amount,
                  };
                  break;
                }
              }
            }
            
            if (foundStake) {
              setHasJoined(true);
              setUserStake(foundStake);
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch game:', error);
      toast.error('Failed to load game');
      navigate('/h2h');
    } finally {
      setLoading(false);
    }
  }, [id, isAuthenticated, currentUserId, navigate]);

  useEffect(() => {
    fetchGameData();
  }, [fetchGameData]);

  // ==================== AUTO-REFRESH FOR LIVE MATCHES ====================
  useEffect(() => {
    // Refresh every 30 seconds if match is live
    if (!game?._isLive) return;
    
    const interval = setInterval(() => {
      console.log('Auto-refreshing live H2H game...');
      fetchGameData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [game?._isLive, fetchGameData]);

  // ==================== TEAM LOGO HELPER ====================
  const getTeamLogo = (teamType = 'home') => {
    if (!game) return null;
    
    if (teamType === 'home') {
      return game.home_club_logo || 
             game.home_logo || 
             game.homeTeamLogo ||
             null;
    }
    return game.away_club_logo || 
           game.away_logo || 
           game.awayTeamLogo ||
           null;
  };

  // ==================== HANDLE JOIN ====================
  const handleOpenJoinForm = () => {
    if (hasJoined) {
      toast.error('You have already joined this game');
      return;
    }
    
    if (!isAuthenticated) {
      toast.error('Please login to join');
      navigate('/login');
      return;
    }
    
    setShowJoinForm(true);
  };

  const handleJoin = async () => {
    if (hasJoined) {
      toast.error('You have already joined this game');
      setShowJoinForm(false);
      return;
    }

    const stake = parseFloat(stakeAmount);
    if (!stake || stake < game?.minimum_stake) {
      toast.error(`Minimum stake is ${formatXPUNT(game?.minimum_stake)}`);
      return;
    }
    
    if (!selectedOutcome) {
      toast.error('Please select an outcome');
      return;
    }

    if (balance < stake) {
      toast.error(`Insufficient balance. You need ${formatXPUNT(stake)}`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await h2hAPI.join(id, {
        stake: stake,
        outcome: selectedOutcome,
      });

      if (response.data.success) {
        toast.success('Joined H2H game successfully!');
        setHasJoined(true);
        setShowJoinForm(false);
        setStakeAmount('');
        setSelectedOutcome('');
        await fetchBalance();
        await fetchGameData();
      } else {
        toast.error(response.data.message || 'Failed to join game');
      }
    } catch (error) {
      console.error('Join error:', error);
      
      if (error.response?.data?.code === 'already_joined') {
        toast.error('You have already joined this game');
        setHasJoined(true);
        setShowJoinForm(false);
        await fetchGameData();
      } else {
        toast.error(error.response?.data?.message || 'Failed to join game');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ==================== HANDLE CLAIM ====================
  const handleClaim = async () => {
    try {
      const response = await h2hAPI.claim(id);
      toast.success('Winnings claimed successfully!');
      setClaimStatus(response.data);
      await fetchBalance();
      await fetchGameData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to claim');
    }
  };

  // ==================== HANDLE SHARE ====================
  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: game?.title || 'H2H Game',
        text: `${game?.home_team} vs ${game?.away_team} - Join the H2H!`,
        url: url,
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied!');
    }
  };

  // ==================== HANDLE SET RESULT (Creator) ====================
  const handleSetResult = () => {
    navigate(`/h2h/${id}/set-result`);
  };

  // ==================== LOADING ====================
  if (loading) {
    return (
      <div className="h2h-detail-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // ==================== NOT FOUND ====================
  if (!game) {
    return (
      <div className="h2h-not-found">
        <h2>Game not found</h2>
        <Link to="/h2h" className="btn btn-primary">
          <FaArrowLeft /> Back to H2H
        </Link>
      </div>
    );
  }

  // ==================== COMPUTED VALUES ====================
  const isCreator = user && (
    String(game.author_id) === String(currentUserId) ||
    String(game.creator_id) === String(currentUserId)
  );
  
  const canJoin = game.status === 'open' && !hasJoined && isAuthenticated && !game._hasStarted;
  const homeLogo = getTeamLogo('home');
  const awayLogo = getTeamLogo('away');
  
  // Check if user can claim winnings
  const canClaim = 
    game._isEnded && 
    hasJoined && 
    userStake && 
    claimStatus?.has_winnings !== false &&
    !claimStatus?.claimed;

  // ==================== RENDER ====================
  return (
    <div className="h2h-detail-page">
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> Back
      </button>

      <div className="h2h-detail-grid">
        {/* Main Content */}
        <div className="detail-main">
          <div className="detail-card">
            {/* Header */}
            <div className="detail-header">
              <div className="detail-meta">
                <span className={`h2h-status ${game.status}`}>
                  {game.status}
                </span>
                {game._isLive && (
                  <span className="h2h-started">
                    <span className="live-dot"></span> Live
                  </span>
                )}
                {game._isEnded && (
                  <span className="h2h-ended">✅ Ended</span>
                )}
              </div>
              <div className="detail-actions">
                <button onClick={handleShare} className="action-btn" title="Share">
                  <FaShare />
                </button>
              </div>
            </div>

            {/* ==================== TEAMS WITH LOGOS ==================== */}
            <div className="detail-match-display">
              {/* Home Team */}
              <div className="detail-team-home">
                {homeLogo ? (
                  <img 
                    src={homeLogo} 
                    alt={game.home_team} 
                    className="detail-team-logo"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) {
                        e.target.nextSibling.style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                <span 
                  className="detail-team-logo-placeholder"
                  style={{ display: homeLogo ? 'none' : 'flex' }}
                >
                  {(game.home_team || 'H').charAt(0).toUpperCase()}
                </span>
                <span className="detail-team-name">{game.home_team}</span>
              </div>

              {/* Score or VS */}
              <div className="detail-match-center">
                {game._hasScore ? (
                  <div className={`h2h-final-score ${game._isLive ? 'live' : 'ended'}`}>
                    <span className="final-score-number">{game.home_score}</span>
                    <span className="final-score-separator">-</span>
                    <span className="final-score-number">{game.away_score}</span>
                    {game._isLive && (
                      <span className="final-score-badge live">
                        <span className="live-dot"></span> LIVE
                      </span>
                    )}
                    {game._isEnded && (
                      <span className="final-score-badge ended">FT</span>
                    )}
                  </div>
                ) : (
                  <span className="detail-vs">vs</span>
                )}
              </div>

              {/* Away Team */}
              <div className="detail-team-away">
                {awayLogo ? (
                  <img 
                    src={awayLogo} 
                    alt={game.away_team} 
                    className="detail-team-logo"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) {
                        e.target.nextSibling.style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                <span 
                  className="detail-team-logo-placeholder"
                  style={{ display: awayLogo ? 'none' : 'flex' }}
                >
                  {(game.away_team || 'A').charAt(0).toUpperCase()}
                </span>
                <span className="detail-team-name">{game.away_team}</span>
              </div>
            </div>

            {/* Stats */}
            <div className="detail-stats">
              <div className="stat-item">
                <FaTrophy className="stat-icon" />
                <div>
                  <span className="stat-value">{formatXPUNT(game.total_pot)}</span>
                  <span className="stat-label">Total Pot</span>
                </div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <FaUsers className="stat-icon" />
                <div>
                  <span className="stat-value">{game.players_count || 0}</span>
                  <span className="stat-label">Players</span>
                </div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <FaClock className="stat-icon" />
                <div>
                  <span className="stat-value">{formatTimeAgo(game.created_at)}</span>
                  <span className="stat-label">Created</span>
                </div>
              </div>
            </div>

            {/* Game Info */}
            <div className="game-info">
              {game.kickoff_time && (
                <div className="info-row">
                  <span>⏰ Kickoff</span>
                  <span>{formatDate(game.kickoff_time)}</span>
                </div>
              )}
              {game.minimum_stake && (
                <div className="info-row">
                  <span>💰 Minimum Stake</span>
                  <span className="gold">{formatXPUNT(game.minimum_stake)}</span>
                </div>
              )}
              {game.result && (
                <div className="info-row">
                  <span>🏆 Result</span>
                  <span className="result">{game.result}</span>
                </div>
              )}
              {game._hasScore && (
                <div className="info-row">
                  <span>⚽ Score</span>
                  <span className="score">{game.home_score} - {game.away_score}</span>
                </div>
              )}
            </div>

            {/* ==================== JOIN SECTION ==================== */}
            {game.status === 'open' && !game._hasStarted && (
              <>
                {hasJoined ? (
                  <div className="already-joined-badge">
                    <FaCheck className="joined-icon" />
                    <div className="joined-info">
                      <strong>You've already joined this game</strong>
                      {userStake && (
                        <div className="stake-details">
                          <span>Your pick: <strong>{userStake.outcome}</strong></span>
                          <span>Your stake: <strong>{formatXPUNT(userStake.stake_amount)}</strong></span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="join-section">
                    {!showJoinForm ? (
                      <button 
                        className="btn btn-primary btn-lg"
                        onClick={handleOpenJoinForm}
                      >
                        <FaHandshake /> Join Game
                      </button>
                    ) : (
                      <div className="join-form">
                        <h3>Join H2H Game</h3>
                        
                        <div className="form-group">
                          <label>Select Outcome</label>
                          <div className="outcome-buttons">
                            {['Home Win', 'Draw', 'Away Win'].map(outcome => (
                              <button
                                key={outcome}
                                type="button"
                                className={`outcome-btn ${selectedOutcome === outcome ? 'selected' : ''}`}
                                onClick={() => setSelectedOutcome(outcome)}
                              >
                                {outcome}
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        <div className="form-group">
                          <label>Stake Amount ({formatXPUNT(game.minimum_stake)} minimum)</label>
                          <input
                            type="number"
                            placeholder="Enter stake amount"
                            value={stakeAmount}
                            onChange={(e) => setStakeAmount(e.target.value)}
                            min={game.minimum_stake}
                            step="0.01"
                          />
                          <small className="balance-hint">
                            Balance: {formatXPUNT(balance)}
                          </small>
                        </div>
                        
                        <div className="form-actions">
                          <button 
                            type="button"
                            onClick={() => setShowJoinForm(false)}
                            className="btn btn-secondary"
                            disabled={submitting}
                          >
                            Cancel
                          </button>
                          <button 
                            onClick={handleJoin}
                            className="btn btn-primary"
                            disabled={submitting || !selectedOutcome || !stakeAmount}
                          >
                            {submitting ? <LoadingSpinner size="small" /> : 'Confirm Join'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* ==================== CLAIM WINNINGS ==================== */}
            {game._isEnded && hasJoined && userStake && (
              <div className="claim-section">
                {claimStatus?.claimed ? (
                  <div className="claimed-badge">
                    <FaCheck /> Winnings Already Claimed
                  </div>
                ) : claimStatus?.has_winnings ? (
                  <div className="claim-available">
                    <div className="claim-info">
                      <FaTrophy className="claim-icon" />
                      <div>
                        <strong>You won {formatXPUNT(claimStatus.winnings)}!</strong>
                        <p>Claim your winnings now</p>
                      </div>
                    </div>
                    <button 
                      className="btn btn-success btn-lg"
                      onClick={handleClaim}
                    >
                      <FaCoins /> Claim Winnings
                    </button>
                  </div>
                ) : (
                  <div className="no-winnings">
                    <p>You didn't win this game. Better luck next time!</p>
                  </div>
                )}
              </div>
            )}

            {/* Stakes Distribution */}
            {game.stakes && Object.keys(game.stakes).length > 0 && (
              <div className="stakes-section">
                <h3>📊 Stakes Distribution</h3>
                <div className="stakes-bars">
                  {Object.entries(game.stakes).map(([outcome, data]) => {
                    const total = Object.values(game.stakes).reduce(
                      (sum, d) => sum + (parseFloat(d.total) || 0), 
                      0
                    );
                    const percentage = total > 0 ? (data.total / total) * 100 : 0;
                    
                    return (
                      <div key={outcome} className="stake-bar-item">
                        <div className="stake-bar-header">
                          <span className="stake-outcome">{outcome}</span>
                          <span className="stake-amount">{formatXPUNT(data.total)}</span>
                          <span className="stake-players">{data.players} players</span>
                        </div>
                        <div className="stake-bar-track">
                          <div 
                            className="stake-bar-fill"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ==================== SIDEBAR ==================== */}
        <div className="detail-sidebar">
          <div className="info-card">
            <h4>ℹ️ Game Info</h4>
            <div className="info-item">
              <span>Created By</span>
              <span>{game.author || 'Unknown'}</span>
            </div>
            <div className="info-item">
              <span>Status</span>
              <span className={game.status}>{game.status}</span>
            </div>
            <div className="info-item">
              <span>Players</span>
              <span>{game.players_count || 0}</span>
            </div>
            {game.total_pot > 0 && (
              <div className="info-item">
                <span>Total Pot</span>
                <span className="gold">{formatXPUNT(game.total_pot)}</span>
              </div>
            )}
          </div>

          {/* Creator Actions */}
          {/* {isCreator && game.status === 'open' && !game._hasStarted && (
            <div className="info-card creator-actions">
              <h4>🎯 Creator Actions</h4>
              <button 
                className="btn btn-primary btn-sm"
                onClick={handleSetResult}
              >
                <FaCheck /> Set Result
              </button>
            </div>
          )} */}

          {/* User Stake Info */}
          {hasJoined && userStake && (
            <div className="info-card">
              <h4>🎫 Your Stake</h4>
              <div className="info-item">
                <span>Your Pick</span>
                <span className="gold">{userStake.outcome}</span>
              </div>
              <div className="info-item">
                <span>Amount</span>
                <span>{formatXPUNT(userStake.stake_amount)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default H2HDetail;